import {
  base_colorAction,
  base_colorAlert,
  base_colorContentBorder,
  base_colorSurfaceSecondary,
  base_contentSelected,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_md,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

export default css`
  &.InvoiceUploader {
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
  }

  .uploader-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spacing(1)};
    padding: ${spacing(6)} ${spacing(4)};
    border: 1px dashed ${base_colorContentBorder};
    border-radius: ${cornerRadius};
    background-color: ${base_colorSurfaceSecondary};
    cursor: pointer;
    text-align: center;
    ${font_sm};
    color: ${text_colorTextSecondary};

    transition: background-color 100ms ease-out;

    &:hover,
    &.is-drag-active {
      border-color: ${base_colorAction};
      background-color: ${base_contentSelected};
    }

    &.is-error {
      border-color: ${base_colorAlert};
    }
  }

  .uploader-cta {
    ${font_md};
    color: ${text_colorTextSecondary};
  }

  .uploader-link {
    font-family: ${font_family_weight_medium};
    color: ${base_colorAction};
  }

  .uploader-hint {
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  .uploader-file {
    display: flex;
    align-items: center;
    gap: ${spacing(3)};
    padding: ${spacing(3)} ${spacing(4)};
    border: 1px solid ${base_colorContentBorder};
    border-radius: ${cornerRadius};
    background-color: ${base_colorSurfaceSecondary};
  }

  .uploader-file-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .uploader-file-meta {
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
    overflow: hidden;
    flex: 1;
    min-width: 0;
  }

  .uploader-file-preview {
    display: flex;
    flex: 1;
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    border-radius: ${cornerRadius};

    &:focus-visible {
      outline: 2px solid ${base_colorAction};
      outline-offset: 2px;
    }
  }

  .uploader-file-name {
    ${font_md};
    color: ${text_colorTextPrimary};
  }

  .uploader-file-status {
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  /* Indeterminate, not a percentage. The upload is a single mutation with no
     byte-progress to report, and a bar that animates to a number nothing
     measured would be inventing precision. A travelling sliver reads as "still
     working" without claiming to know how far along it is. */
  .uploader-file-progress {
    height: ${spacing(1)};
    margin-top: ${spacing(1)};
    border-radius: ${cornerRadius};
    background-color: ${base_colorContentBorder};
    overflow: hidden;
  }

  .uploader-file-progress-bar {
    /* Both of these are spans inside a text row, so the fill needs blockifying
       before width/height mean anything — as an inline box it measured 0×0 and
       the track rendered empty. The track escapes this only by being a flex
       item of .uploader-file-meta. */
    display: block;
    height: 100%;
    width: 40%;
    border-radius: ${cornerRadius};
    background-color: ${base_colorAction};
    animation: uploader-progress-travel 1.1s ease-in-out infinite;
  }

  @keyframes uploader-progress-travel {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(250%);
    }
  }

  /* Reduced motion still needs to say "working" — a filled track reads as
     indeterminate without anything moving. */
  @media (prefers-reduced-motion: reduce) {
    .uploader-file-progress-bar {
      width: 100%;
      animation: none;
    }
  }

  .ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* Trash affordance is hover/focus-revealed, but stays focusable and operable
     for keyboard users (opacity, not display, so the tab stop survives). */
  .uploader-file-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: ${spacing(1)};
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: ${cornerRadius};
    opacity: 0;
    transition: opacity 100ms ease-out;

    &:focus-visible {
      opacity: 1;
      outline: 2px solid ${base_colorAction};
      outline-offset: 1px;
    }
  }

  .uploader-file:hover .uploader-file-remove,
  .uploader-file:focus-within .uploader-file-remove {
    opacity: 1;
  }

  .uploader-error {
    ${font_sm};
    color: ${base_colorAlert};
    margin: 0;
  }
`
